import moment from 'moment';
import React, { useEffect, useState } from 'react';

interface DateRangePickerProps {
  onSearch?: (startDate: string, endDate: string) => void;
  t: (key: string) => string;
  vehicleType?: string;
  onDateRangeChange?: (startDate: string, endDate: string) => void;
  filters: {
    vehicleType: string;
    date?: string;
    limit: number;
    offset: number;
  };
  onFilterChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, field: string) => void;
  resetKey?: number;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  onSearch,
  t,
  onFilterChange,
  resetKey = 0,
}) => {
  const [selectionType, setSelectionType] = useState(() => {
    return localStorage.getItem('selectionType') || 'day';
  });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const storedStartDate = localStorage.getItem('startDate');
    const storedEndDate = localStorage.getItem('endDate');
    const storedSelectionType = localStorage.getItem('selectionType');

    if (storedStartDate && storedEndDate && storedSelectionType) {
      setStartDate(storedStartDate);
      setEndDate(storedEndDate);
      setSelectionType(storedSelectionType);
    } else {
      const today = moment().format('YYYY-MM-DD');
      setStartDate(today);
      setEndDate(calculateEndDate(today, selectionType));
      localStorage.setItem('startDate', today);
      localStorage.setItem('endDate', calculateEndDate(today, selectionType));
      localStorage.setItem('selectionType', selectionType);
    }
  }, []);

  useEffect(() => {
    if (resetKey > 0) {
      const today = moment().format('YYYY-MM-DD');
      setStartDate(today);
      setEndDate(calculateEndDate(today, 'day'));
      setSelectionType('day');
      localStorage.removeItem('startDate');
      localStorage.removeItem('endDate');
      localStorage.removeItem('selectionType');

      const syntheticEvent = {
        target: { value: today }
      } as React.ChangeEvent<HTMLInputElement>;
      onFilterChange(syntheticEvent, 'date');
    }
  }, [resetKey]);

  const calculateEndDate = (start: string, type: string) => {
    if (!start) return '';
    const date = moment(start, 'YYYY-MM-DD');
    switch (type) {
      case 'day':
        return date.format('YYYY-MM-DD');
      case 'week':
        return date.add(6, 'days').format('YYYY-MM-DD');
      case 'month':
        return date.endOf('month').format('YYYY-MM-DD');
      default:
        return date.format('YYYY-MM-DD');
    }
  };

  const handleDateChange = async (newStartDate: string, newEndDate: string) => {
    setIsLoading(true);
    try {
      if (onSearch) {
        await onSearch(newStartDate, newEndDate);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value;
    setStartDate(newStartDate);
    localStorage.setItem('startDate', newStartDate);

    const syntheticEvent = {
      target: { value: newStartDate }
    } as React.ChangeEvent<HTMLInputElement>;
    onFilterChange(syntheticEvent, 'date');

    let newEndDate = newStartDate;
    if (selectionType !== 'custom') {
      newEndDate = calculateEndDate(newStartDate, selectionType);
      setEndDate(newEndDate);
      localStorage.setItem('endDate', newEndDate);
    }

    handleDateChange(newStartDate, selectionType === 'custom' ? endDate : newEndDate);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = e.target.value;
    setEndDate(newEndDate);
    localStorage.setItem('endDate', newEndDate);

    if (startDate) {
      handleDateChange(startDate, newEndDate);
    }
  };

  const handleSelectionTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectionType(value);
    localStorage.setItem('selectionType', value);

    if (value !== 'custom' && startDate) {
      const newEndDate = calculateEndDate(startDate, value);
      setEndDate(newEndDate);
      localStorage.setItem('endDate', newEndDate);

      handleDateChange(startDate, newEndDate);
    }
  };

  return (
    <div className="card p-4 randeMainContainer" style={{ margin: 'auto', background: '#F8F9FA' }}>
      <div className='daterangeContainer'>
        <div className="mb-3">
          <label className="form-label">{t("selection_Type")}</label>
          <select
            className="form-select"
            value={selectionType}
            onChange={handleSelectionTypeChange}
            disabled={isLoading}
          >
            <option value="day">{t("single_day")}</option>
            <option value="week">{t("Week")}</option>
            <option value="month">{t("Month")}</option>
            {/* <option value="custom">{t("custom_Range")}</option> */}
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label">{selectionType === 'custom' ? t("start_Date") : t("Date")}</label>
          <input
            type="date"
            className="form-control"
            value={startDate}
            onChange={handleStartDateChange}
            disabled={isLoading}
          />
        </div>
        {startDate && (
          <div className="alert range text-center" role="alert" style={{ gap: '0px', padding: '10px' }}>
            <strong>{t("selected_range")}</strong>{' '}
            {moment(startDate).format('DD/MM/YY')} to{' '}
            {moment(selectionType === 'custom' ? endDate : calculateEndDate(startDate, selectionType)).format('DD/MM/YY')}
            {isLoading && (
              <div className="mt-2">
                <div className="spinner-border spinner-border-sm text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {/* {selectionType === 'custom' && (
        <div className="mb-3">
          <label className="form-label">{t("end_Date")}</label>
          <input
            type="date"
            className="form-control"
            value={endDate}
            onChange={handleEndDateChange}
            min={startDate}
            disabled={isLoading}
          />
        </div>
      )} */}
    </div>
  );
};

export default DateRangePicker;